/**
 * Checklist action type configs — Wave 2b output.
 *
 * Each config is a FE-only enrichment of the manifest.json entry for that type.
 * Merge happens here at module load via mergeWithManifest() — ActionCard reads
 * from the already-merged config directly; no runtime merging needed.
 *
 * Fields sourced from manifest.json (authoritative documentation data):
 *   - label, category, endpoints, be_persistence, be_validation, role_gating,
 *     archive_guard, request_body_shape, validation_rules, task_effect,
 *     lead_effect, permission, source, top_scenarios, _lead_review
 *
 * Fields added by FE configs (override manifest where they collide):
 *   - try_it        — button label, cell_component_import, prefill values
 *   - pre_flight_diff — human-readable lines shown before "Try it" is clicked
 *   - has_step2_throttle — enables drift-simulation toggle for multi-step types
 *   - validation_laboratory — pre-filled bad-input test cases (6 loose-validation types + LEAD_STATUS_CHANGED)
 */

import manifest from '../manifest.json';
import CONFIRMATION from './CONFIRMATION';
import EMAIL_SENT from './EMAIL_SENT';
import BOOK_APPOINTMENT from './BOOK_APPOINTMENT';
import ADD_NOTE from './ADD_NOTE';
import CURRENCY_AMOUNT from './CURRENCY_AMOUNT';
import LEAD_NEXT_ACTION from './LEAD_NEXT_ACTION';
import WHATSAPP_SENT from './WHATSAPP_SENT';
import SMS_SENT from './SMS_SENT';
import PHONE_CALLED from './PHONE_CALLED';
import LOG_PHONE_CALL from './LOG_PHONE_CALL';
import SEND_CONSENT_FORM from './SEND_CONSENT_FORM';
import SEND_REVIEW_REQUEST from './SEND_REVIEW_REQUEST';
import SEND_PAYMENT_LINK from './SEND_PAYMENT_LINK';
import SEND_TREATMENT_PLAN from './SEND_TREATMENT_PLAN';
import OPEN_FOLLOW_UP from './OPEN_FOLLOW_UP';
import SCHEDULE_REMINDER from './SCHEDULE_REMINDER';
import RECORD_ATTENDANCE from './RECORD_ATTENDANCE';
import RECORD_FAILED_TO_ATTEND from './RECORD_FAILED_TO_ATTEND';
import START_TREATMENT from './START_TREATMENT';
import COMPLETE_TREATMENT from './COMPLETE_TREATMENT';
import UPLOAD_DOCUMENT from './UPLOAD_DOCUMENT';
import LINKED_CHECKLIST from './LINKED_CHECKLIST';
import LEAD_STATUS_CHANGED from './LEAD_STATUS_CHANGED';
import FREE_TEXT from './FREE_TEXT';
import MULTIPLE_CHOICE from './MULTIPLE_CHOICE';
import YES_NO_TOGGLE from './YES_NO_TOGGLE';
import PAYMENT_METHOD from './PAYMENT_METHOD';
import STAFF_PICKER from './STAFF_PICKER';
import DATE_FIELD from './DATE_FIELD';

/**
 * mergeWithManifest — combines a FE config object with its manifest.json entry.
 *
 * Manifest fields are laid down first (authoritative documentation data).
 * The FE config is then spread on top, so FE-only fields are added and any
 * intentional FE overrides (e.g. a more specific label) take precedence.
 *
 * @param {object} config — raw FE config (must have a `manifestKey` string)
 * @returns {object} merged config ready for ActionCard consumption
 */
const mergeWithManifest = (config) => {
    const m = manifest[config.manifestKey] || {};
    return {
        // Manifest fields first (authoritative documentation data)
        label: m.label,
        category: m.category,
        endpoints: m.endpoints,
        be_persistence: m.be_persistence,
        be_validation: m.be_validation,
        role_gating: m.role_gating,
        archive_guard: m.archive_guard,
        request_body_shape: m.request_body_shape,
        validation_rules: m.validation_rules,
        task_effect: m.task_effect,
        lead_effect: m.lead_effect,
        permission: m.permission,
        source: m.source,
        top_scenarios: m.top_scenarios,
        _lead_review: m._lead_review,
        // Then spread the FE config (overrides manifest where they collide; adds FE-only fields)
        ...config,
    };
};

/**
 * CONFIGS — keyed by type code string (matches CHECKLIST_ITEM_TYPE enum values).
 * Use for O(1) lookup by type when rendering a specific card.
 * Each entry is already merged with manifest.json data via mergeWithManifest().
 */
export const CONFIGS = {
    CONFIRMATION: mergeWithManifest(CONFIRMATION),
    EMAIL_SENT: mergeWithManifest(EMAIL_SENT),
    BOOK_APPOINTMENT: mergeWithManifest(BOOK_APPOINTMENT),
    ADD_NOTE: mergeWithManifest(ADD_NOTE),
    CURRENCY_AMOUNT: mergeWithManifest(CURRENCY_AMOUNT),
    LEAD_NEXT_ACTION: mergeWithManifest(LEAD_NEXT_ACTION),
    WHATSAPP_SENT: mergeWithManifest(WHATSAPP_SENT),
    SMS_SENT: mergeWithManifest(SMS_SENT),
    PHONE_CALLED: mergeWithManifest(PHONE_CALLED),
    LOG_PHONE_CALL: mergeWithManifest(LOG_PHONE_CALL),
    SEND_CONSENT_FORM: mergeWithManifest(SEND_CONSENT_FORM),
    SEND_REVIEW_REQUEST: mergeWithManifest(SEND_REVIEW_REQUEST),
    SEND_PAYMENT_LINK: mergeWithManifest(SEND_PAYMENT_LINK),
    SEND_TREATMENT_PLAN: mergeWithManifest(SEND_TREATMENT_PLAN),
    OPEN_FOLLOW_UP: mergeWithManifest(OPEN_FOLLOW_UP),
    SCHEDULE_REMINDER: mergeWithManifest(SCHEDULE_REMINDER),
    RECORD_ATTENDANCE: mergeWithManifest(RECORD_ATTENDANCE),
    RECORD_FAILED_TO_ATTEND: mergeWithManifest(RECORD_FAILED_TO_ATTEND),
    START_TREATMENT: mergeWithManifest(START_TREATMENT),
    COMPLETE_TREATMENT: mergeWithManifest(COMPLETE_TREATMENT),
    UPLOAD_DOCUMENT: mergeWithManifest(UPLOAD_DOCUMENT),
    LINKED_CHECKLIST: mergeWithManifest(LINKED_CHECKLIST),
    LEAD_STATUS_CHANGED: mergeWithManifest(LEAD_STATUS_CHANGED),
    FREE_TEXT: mergeWithManifest(FREE_TEXT),
    MULTIPLE_CHOICE: mergeWithManifest(MULTIPLE_CHOICE),
    YES_NO_TOGGLE: mergeWithManifest(YES_NO_TOGGLE),
    PAYMENT_METHOD: mergeWithManifest(PAYMENT_METHOD),
    STAFF_PICKER: mergeWithManifest(STAFF_PICKER),
    DATE_FIELD: mergeWithManifest(DATE_FIELD),
};

/**
 * ORDERED_CONFIGS — array in plan order (id: 1–29).
 * Use for rendering the card grid sequentially.
 */
export const ORDERED_CONFIGS = Object.values(CONFIGS).sort((a, b) => a.id - b.id);
